/**
 * The `product-media` element manages the display of a set of product images, allowing the user to
 * navigate between them using thumbnail previews and arrow controls. It handles the active state
 * of the currently displayed image, and provides event handlers for interacting with the gallery.
 *
 * This element is typically used within a product detail page to showcase the available media for
 * a given product.
 */
class ProductMedia extends HTMLElement {
  static selectors = {
    main: '[js-main]',
    thumbs: '[js-thumbs]',
    arrows: '[js-arrow]',
    active: '.is-active',
  };

  static ACTIVE_CLASS = 'is-active';

  constructor() {
    super();
  }

  /**
   * Sets the active image in the product media gallery.
   *
   * @param {number} index - The index of the image to set as active.
   * @param {number} [direction=0] - The direction to move the active image, -1 for previous, 1 for next.
   * @param {Boolean} [useScrollIntoView=true]
   * @returns {void}
   */
  setActiveImage(index, direction = 0, useScrollIntoView = true) {
    this.activeIndex = Number(index) + Number(direction);

    if (this.activeIndex > this.maxIndex) {
      this.activeIndex = 0;
      // this.thumbs.scrollTop = 0;
    }

    if (this.activeIndex < 0) {
      this.activeIndex = this.maxIndex;
      // this.thumbs.scrollTop = this.thumbs.scrollHeight;
    }

    // this.thumbs.scrollTop = this.activeIndex * this.thumbSize;
    if (!useScrollIntoView) return;

    this.querySelectorAll(ProductMedia.selectors.active).forEach((element) => {
      element.classList.remove(ProductMedia.ACTIVE_CLASS);
    });

    this.querySelectorAll(`[data-index="${this.activeIndex}"]`).forEach(
      (element) => {
        element.classList.add(ProductMedia.ACTIVE_CLASS);
        if (!element.closest('[js-main]')) return;

        element.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
          inline: 'start',
        });
      },
    );
  }

  handleMainScroll() {
    const containerWidth = this.main.offsetWidth;
    const scrollPosition = this.main.scrollLeft;
    const newActiveIndex = Math.round(scrollPosition / containerWidth);

    if (newActiveIndex !== this.activeIndex) {
      this.setActiveImage(newActiveIndex, 0, false);
    }
  }

  /**
   * Initializes the thumbnail scrolling and click event handlers for the product media component.
   * This method is called in the `connectedCallback` of the `ProductMedia` custom element.
   * It adds a wheel event listener to the thumbnail container to allow scrolling the thumbnails,
   * and a mouseover event listener to each thumbnail to set the active image when a thumbnail is hovered.
   */
  initThumbs() {
    if (!this.thumbs) return;

    this.thumbSize =
      this.thumbs.firstElementChild.getBoundingClientRect().height;

    this.thumbs.addEventListener('wheel', (event) => {
      event.preventDefault();

      const delta = event.deltaY;

      this.thumbs.scrollTop += delta;
    });

    this.thumbs.addEventListener('mouseover', (event) => {
      const target = event.target.closest('[data-index]');

      if (!target) return;

      this.setActiveImage(Number(target.getAttribute('data-index')));
    });
  }

  adjustThumbsHeight() {
    this.style.setProperty(
      '--max-height',
      this.main.getBoundingClientRect().height + 'px',
    );
  }

  /**
   * Initializes the main image container for the product media component.
   * This method is called in the `connectedCallback` of the `ProductMedia` custom element.
   * It sets the `maxIndex` property to the index of the last image in the main container.
   */
  initMain() {
    if (!this.main) return;

    this.maxIndex = this.main.querySelectorAll('img').length - 1;

    window.addEventListener('load', this.adjustThumbsHeight.bind(this));
    window.addEventListener('resize', this.adjustThumbsHeight.bind(this));

    this.main.addEventListener('scroll', this.handleMainScroll.bind(this));

    this.adjustThumbsHeight();
  }

  /**
   * Handles the click event on the product media component's arrow elements.
   * This method is called when an arrow element is clicked, and it updates the active image
   * based on the direction specified by the clicked arrow.
   *
   * @param {Event} event - The click event object.
   */
  handleArrowClick(event) {
    event.preventDefault();

    const arrow = event.target.closest(ProductMedia.selectors.arrows);

    this.setActiveImage(
      this.activeIndex,
      Number(arrow.getAttribute('data-direction')),
    );
  }

  /**
   * Initializes the arrow click event handlers for the product media component.
   * This method is called in the `connectedCallback` of the `ProductMedia` custom element.
   * It adds a click event listener to each arrow element, which calls the `handleArrowClick` method when clicked.
   */
  initArrows() {
    if (!this.arrows) return;

    this.arrows.forEach((arrow) => {
      arrow.addEventListener('click', this.handleArrowClick.bind(this));
    });
  }

  connectedCallback() {
    this.main = this.querySelector(ProductMedia.selectors.main);
    this.thumbs = this.querySelector(ProductMedia.selectors.thumbs);
    this.arrows = this.querySelectorAll(ProductMedia.selectors.arrows);

    this.activeIndex = 0;

    this.initMain();
    this.initThumbs();
    this.initArrows();

    if (window.innerWidth < 750) {
      this.setActiveImage(1);
      setTimeout(() => {
        this.setActiveImage(0);
      }, 0);
    }
  }
}

customElements.define('product-media', ProductMedia);
